<script setup lang="ts">
  import { onMounted, ref, watch } from 'vue';
  import {
    type DocumentSendFilters,
    type DocumentSendProtheusParty,
    DocumentSendDocumentType,
    DocumentSendFormat,
  } from '@/entities/DocumentSend';
  import {
    buildProtheusPartyKey,
    protheusApi,
    ProtheusRecipientCombobox,
    type ProtheusClient,
    type ProtheusRecipientOption,
    type ProtheusSupplier,
  } from '@/entities/Protheus';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { useCustomToast } from '@/shared/lib';
  import { DatePicker, FilterModal, IconImage, ThemeProvider } from '@/shared/ui';

  const WITH_ELECTRONIC_COPY = 'WITH_ELECTRONIC_COPY' as const;

  type DocumentTypeOption = DocumentSendDocumentType | typeof WITH_ELECTRONIC_COPY;

  interface DocumentTypeEntry {
    code: DocumentTypeOption;
    label: string;
  }

  interface FormatEntry {
    code: DocumentSendFormat;
    label: string;
  }

  const DOCUMENT_TYPE_OPTIONS: DocumentTypeEntry[] = [
    { code: DocumentSendDocumentType.MANUAL, label: 'Manual' },
    { code: DocumentSendDocumentType.PROCEDURE, label: 'Procedimento' },
    { code: DocumentSendDocumentType.WORK_INSTRUCTION, label: 'Instrução de Trabalho' },
    { code: DocumentSendDocumentType.DATA, label: 'Dados' },
    { code: WITH_ELECTRONIC_COPY, label: 'Com Cópia Eletrônica' },
  ];

  const FORMAT_OPTIONS: FormatEntry[] = [
    { code: DocumentSendFormat.ELECTRONIC, label: 'Eletrônico' },
    { code: DocumentSendFormat.PHYSICAL, label: 'Físico' },
    // Hidden on purpose: sends already stored as ELECTRONIC_PHYSICAL still depend on this format.
    // { code: DocumentSendFormat.ELECTRONIC_PHYSICAL, label: 'Eletrônico e físico' },
  ];

  const props = defineProps<{
    currentFilters?: DocumentSendFilters;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:filter', filters: DocumentSendFilters): void;
  }>();

  const selectedDocumentTypes = ref<Set<DocumentTypeOption>>(new Set());
  const selectedFormats = ref<Set<DocumentSendFormat>>(new Set());
  const selectedClient = ref<DocumentSendProtheusParty | undefined>();
  const selectedSupplier = ref<DocumentSendProtheusParty | undefined>();
  const selectedDate = ref<Date | null>(null);
  const isDatePickerOpen = ref(false);

  const toast = useCustomToast();

  const clients = ref<ProtheusClient[]>([]);
  const suppliers = ref<ProtheusSupplier[]>([]);
  const loadingClients = ref(false);
  const loadingSuppliers = ref(false);

  const clientOptions = ref<ProtheusRecipientOption[]>([]);
  const supplierOptions = ref<ProtheusRecipientOption[]>([]);

  const toRecipientOption = (party: ProtheusClient | ProtheusSupplier) => ({
    id: buildProtheusPartyKey(party),
    name: party.name,
  });

  const toPartyKey = (party?: DocumentSendProtheusParty) =>
    party ? buildProtheusPartyKey(party) : '';

  const findPartyByKey = (
    parties: (ProtheusClient | ProtheusSupplier)[],
    key: string
  ): DocumentSendProtheusParty | undefined => {
    const party = parties.find((item) => buildProtheusPartyKey(item) === key);
    return party ? { code: party.code, store: party.store } : undefined;
  };

  const parseSendDate = (value?: string): Date | null => {
    if (!value) return null;

    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!isoMatch) return null;

    const [, year, month, day] = isoMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatSendDate = (value: Date | null): string | undefined => {
    if (!value) return undefined;

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (value: Date | null): string => {
    if (!value) return 'Selecione uma data';
    return new Intl.DateTimeFormat('pt-BR').format(value);
  };

  const applyCurrentFilters = (filters?: DocumentSendFilters) => {
    const nextDocumentTypes = new Set<DocumentTypeOption>();
    (filters?.documentTypes ?? []).forEach((code) => nextDocumentTypes.add(code));
    if (filters?.hasElectronicCopy) {
      nextDocumentTypes.add(WITH_ELECTRONIC_COPY);
    }
    selectedDocumentTypes.value = nextDocumentTypes;

    selectedFormats.value = new Set(filters?.formats ?? []);
    selectedClient.value = filters?.protheusClient;
    selectedSupplier.value = filters?.protheusSupplier;
    selectedDate.value = parseSendDate(filters?.sendDate);
  };

  const toggleDocumentType = (code: DocumentTypeOption) => {
    const next = new Set(selectedDocumentTypes.value);
    if (next.has(code)) {
      next.delete(code);
    } else {
      next.add(code);
    }
    selectedDocumentTypes.value = next;
  };

  const toggleFormat = (code: DocumentSendFormat) => {
    const next = new Set(selectedFormats.value);
    if (next.has(code)) {
      next.delete(code);
    } else {
      next.add(code);
    }
    selectedFormats.value = next;
  };

  const handleClientChange = (key: string) => {
    selectedClient.value = findPartyByKey(clients.value, key);
  };

  const handleSupplierChange = (key: string) => {
    selectedSupplier.value = findPartyByKey(suppliers.value, key);
  };

  const clearLocalFilters = () => {
    selectedDocumentTypes.value = new Set();
    selectedFormats.value = new Set();
    selectedClient.value = undefined;
    selectedSupplier.value = undefined;
    selectedDate.value = null;
  };

  // Modal dismissal is handled by the parent via `useTableFilterModal`,
  // which calls `modal.close()` immediately after `onUpdate:filter` fires.
  const applyFilters = () => {
    const documentTypes: DocumentSendDocumentType[] = [];
    let hasElectronicCopy = false;

    selectedDocumentTypes.value.forEach((code) => {
      if (code === WITH_ELECTRONIC_COPY) {
        hasElectronicCopy = true;
      } else {
        documentTypes.push(code);
      }
    });

    const payload: DocumentSendFilters = {};

    if (documentTypes.length > 0) {
      payload.documentTypes = documentTypes;
    }
    if (selectedFormats.value.size > 0) {
      payload.formats = Array.from(selectedFormats.value);
    }
    if (hasElectronicCopy) {
      payload.hasElectronicCopy = true;
    }
    if (selectedClient.value) {
      payload.protheusClient = selectedClient.value;
    }
    if (selectedSupplier.value) {
      payload.protheusSupplier = selectedSupplier.value;
    }
    const formattedDate = formatSendDate(selectedDate.value);
    if (formattedDate) {
      payload.sendDate = formattedDate;
    }

    emit('update:filter', payload);
  };

  const fetchClients = async () => {
    loadingClients.value = true;
    try {
      clients.value = await protheusApi.getClients();
      clientOptions.value = clients.value.map(toRecipientOption);
    } catch {
      clients.value = [];
      clientOptions.value = [];
      toast.showErrorToast({
        title: 'Erro ao carregar clientes',
        message: 'Tente novamente em instantes.',
        type: 'error',
      });
    } finally {
      loadingClients.value = false;
    }
  };

  const fetchSuppliers = async () => {
    loadingSuppliers.value = true;
    try {
      suppliers.value = await protheusApi.getSuppliers();
      supplierOptions.value = suppliers.value.map(toRecipientOption);
    } catch {
      suppliers.value = [];
      supplierOptions.value = [];
      toast.showErrorToast({
        title: 'Erro ao carregar fornecedores',
        message: 'Tente novamente em instantes.',
        type: 'error',
      });
    } finally {
      loadingSuppliers.value = false;
    }
  };

  onMounted(() => {
    applyCurrentFilters(props.currentFilters);
    void fetchClients();
    void fetchSuppliers();
  });

  watch(
    () => props.currentFilters,
    (next) => applyCurrentFilters(next),
    { deep: true }
  );
</script>

<template>
  <FilterModal
    @close="emit('close')"
    @clear-filters="clearLocalFilters"
    @apply-filters="applyFilters"
  >
    <template #content>
      <ThemeProvider class="overflow-auto">
        <div
          class="px-6"
          data-testid="document-send-control-filter-modal"
        >
          <div class="py-4 border-b border-gray-02 grid grid-cols-2 gap-6">
            <div>
              <h4 class="font-medium text-gray-07 mb-4">
                Tipo de Documento
              </h4>
              <div class="flex flex-col gap-3">
                <button
                  v-for="option in DOCUMENT_TYPE_OPTIONS"
                  :key="option.code"
                  type="button"
                  :class="[
                    'py-2 px-3 rounded-lg text-left text-sm',
                    selectedDocumentTypes.has(option.code)
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07',
                  ]"
                  :data-testid="`document-type-option-${option.code}`"
                  @click="toggleDocumentType(option.code)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>

            <div>
              <h4 class="font-medium text-gray-07 mb-4">
                Formato
              </h4>
              <div class="flex flex-col gap-3">
                <button
                  v-for="option in FORMAT_OPTIONS"
                  :key="option.code"
                  type="button"
                  :class="[
                    'py-2 px-3 rounded-lg text-left text-sm',
                    selectedFormats.has(option.code)
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07',
                  ]"
                  :data-testid="`format-option-${option.code}`"
                  @click="toggleFormat(option.code)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </div>

          <div class="py-4 border-b border-gray-02">
            <h4 class="font-medium text-gray-07 mb-4">
              Cliente
            </h4>
            <ProtheusRecipientCombobox
              data-testid="filter-client-select"
              trigger-class="h-11 rounded-lg px-4"
              :model-value="toPartyKey(selectedClient)"
              :options="clientOptions"
              :loading="loadingClients"
              placeholder="Selecione"
              search-placeholder="Buscar cliente..."
              not-found-message="Nenhum cliente encontrado."
              @update:model-value="handleClientChange"
            />
          </div>

          <div class="py-4 border-b border-gray-02">
            <h4 class="font-medium text-gray-07 mb-4">
              Fornecedor
            </h4>
            <ProtheusRecipientCombobox
              data-testid="filter-supplier-select"
              trigger-class="h-11 rounded-lg px-4"
              :model-value="toPartyKey(selectedSupplier)"
              :options="supplierOptions"
              :loading="loadingSuppliers"
              placeholder="Selecione"
              search-placeholder="Buscar fornecedor..."
              not-found-message="Nenhum fornecedor encontrado."
              @update:model-value="handleSupplierChange"
            />
          </div>

          <div class="py-4">
            <h4 class="font-medium text-gray-07 mb-4">
              Data
            </h4>
            <button
              type="button"
              class="flex items-center bg-white-01 px-4 py-3 border border-gray-02 rounded-lg text-gray-03 focus:outline-none focus:border-gray-07 font-medium gap-2 min-w-[236px]"
              data-testid="filter-date-button"
              @click="isDatePickerOpen = true"
            >
              <IconImage
                :src="CalendarIcon"
                alt="Calendário"
                class="h-5 w-5"
              />
              <span>{{ formatDateForDisplay(selectedDate) }}</span>
            </button>
            <DatePicker
              v-model:selected-date="selectedDate"
              v-model:is-open="isDatePickerOpen"
              @apply="isDatePickerOpen = false"
              @cancel="isDatePickerOpen = false"
            />
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
